<template>
	<div class="opportunity-card-summary" @click="emit('select', opportunity)">
		<div class="header">
			<div class="lead">
				<span class="type-icon">
					<opportunity-card-flag-icon
						:type="opportunity.type"
						:sales-process-status="opportunity.sales_process_status"
						:launch-date-ms="opportunity.launch_date_ms"
					/>
				</span>
				<span class="lead-text">{{ leadText }}</span>
			</div>
		</div>

		<span v-if="leadIsPrice" class="address">{{ opportunity.address }}</span>
		<span class="secondary-line">{{ secondaryLine }}</span>
		<span v-if="assetCategoryLine" class="secondary-line">{{ assetCategoryLine }}</span>
		<span v-if="collaborativeBrokers.length > 0" class="collaborative-brokers">
			{{
				$t(
					collaborativeBrokers.length === 1
						? "opportunities-table.columns.collaborative-broker"
						: "opportunities-table.columns.collaborative-brokers"
				)
			}}
			· <span class="names">{{ collaborativeBrokers.join(", ") }}</span>
		</span>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { type Opportunity, OpportunityType } from "@/model/DataModel.ts"
import OpportunityCardFlagIcon from "@/components/opportunities/OpportunityCardFlagIcon.vue"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { collaborativeBrokerNames } from "@/utils/brokers"
import { formatQuantity } from "@/utils/format"
import { formatOpportunityArea } from "@/utils/opportunityUnitFields"

const props = defineProps<{
	opportunity: Opportunity
}>()

const emit = defineEmits<{
	select: [opportunity: Opportunity]
}>()

const { opportunity } = props
const { t } = useI18n()
const { areaUnit, areaUnitSymbol } = useUnitSystem()

function formatPrice(value: number | null | undefined): string {
	if (!value) return ""
	return formatQuantity(Math.round(value))
}

// `price` is 0 (not null) from the API when there's no asking price, so `||` not `??`.
const displayedPrice = computed(() => opportunity.price || opportunity.priceExpectation)
const leadIsPrice = computed(() => Boolean(displayedPrice.value))

const leadText = computed(() => {
	if (displayedPrice.value) return `${formatPrice(displayedPrice.value)} $`
	if (opportunity.address) return opportunity.address
	return `#${opportunity.id}`
})

const secondaryLine = computed(() => {
	const parts = [opportunity.city]
	if (opportunity.superficy) {
		parts.push(`${formatOpportunityArea(opportunity.superficy, areaUnit.value)} ${areaUnitSymbol.value}`)
	}
	return parts.filter(Boolean).join(" · ")
})

function formatAssetType(values: string[] | null | undefined): string {
	if (!values || values.length === 0) return ""
	return values.map(value => t(`property.assetType.${value}`)).join(", ")
}

function formatLandCategory(values: string[] | null | undefined): string {
	if (!values || values.length === 0) return ""
	return values.map(value => t(`property.landCategory.${value}`)).join(", ")
}

function formatLandSubcategory(values: string[] | null | undefined): string {
	if (!values || values.length === 0) return ""
	return values
		.map(value => {
			const key = value
				.split("_")
				.map(word => word.charAt(0).toUpperCase() + word.slice(1))
				.join("")
			return t(`property.landSubcategory.${key}`)
		})
		.join(", ")
}

const assetCategoryLine = computed(() =>
	[
		formatAssetType(opportunity.assetType),
		formatLandCategory(opportunity.landCategoryNew),
		formatLandSubcategory(opportunity.landSubcategory),
	]
		.filter(Boolean)
		.join(" · ")
)

const collaborativeBrokers = computed(() =>
	opportunity.type === OpportunityType.ListingLanderz ? [] : collaborativeBrokerNames(opportunity)
)
</script>

<style scoped>
.opportunity-card-summary {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xs);
	padding: var(--spacing-sm) var(--spacing-md) var(--spacing-md);
	border: 1px solid var(--neutral-100);
	border-radius: var(--border-radius-md);
	background-color: var(--neutral-0);
	cursor: pointer;
	transition: box-shadow 0.15s ease;

	&:hover {
		box-shadow: var(--shadow-md);
	}

	.header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: var(--spacing-sm);
		min-width: 0;
	}

	.lead {
		display: block;
		flex: 1 1 auto;
		overflow: hidden;
		min-width: 0;
	}

	.type-icon {
		display: block;
		float: left;
		margin-right: var(--spacing-xxs);
		transform: scale(0.75);
		transform-origin: top left;
	}

	.lead-text {
		display: block;
		margin-top: var(--spacing-xxs);
		color: var(--neutral-900);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
	}

	.address {
		color: var(--neutral-900);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-regular);
	}

	.secondary-line {
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
		margin-top: var(--spacing-xxs);
	}

	.collaborative-brokers {
		margin-top: var(--spacing-xs);
		padding-top: var(--spacing-xs);
		border-top: 1px solid var(--neutral-100);
		color: var(--neutral-600);
		font-size: var(--font-size-xs);

		.names {
			color: var(--neutral-900);
			font-weight: var(--font-weight-medium);
		}
	}
}
</style>
